import { localizedStrings } from '@core/strings'
import React, { useEffect, useState } from 'react'
import { Box, Flex, Text } from '@common/fundamentals'
import { Button, IconButton, TextInput } from '@common/material'
import { ISiteBoxDocumentConfigResult } from '@core/entities/SiteBoxDocumentConfig'
import {
  ArrowBack,
  KeyboardArrowLeft,
  KeyboardArrowRight,
} from '@mui/icons-material'
import { QueryResultDetails } from './QueryResultDetails'
import { AppraisalStatusButtons } from './AppraisalStatusButtons'
import { useUpdateQueryAppraisal } from '@core/react-query/features/siteBoxDocuments'

type UserAppraisalType = 'PASS' | 'FAIL' | 'NA' | null

type QueryResultAppraisalViewProps = {
  documentId: number
  results: ISiteBoxDocumentConfigResult[]
  isDocumentAppraised: boolean
  initialIndex?: number
  onBackClick: () => void
  onComplete: () => void
  setPDFSearchState: (search: string, pageNumber?: number) => void
  clearPDFSearchState: () => void
  searchResultInfo: { current: number; total: number } | null
  onNextResult: () => void
  onPreviousResult: () => void
  canUserAppraiseDocument: boolean
  documentEngineTemplateName: string
}

export const QueryResultAppraisalView: React.FC<
  QueryResultAppraisalViewProps
> = ({
  documentId,
  results,
  isDocumentAppraised,
  initialIndex = 0,
  onBackClick,
  onComplete,
  setPDFSearchState,
  clearPDFSearchState,
  searchResultInfo,
  onNextResult,
  onPreviousResult,
  canUserAppraiseDocument,
  documentEngineTemplateName,
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const [comment, setComment] = useState(
    results[initialIndex]?.additionalComment || '',
  )
  const [selectedResponse, setSelectedResponse] = useState<UserAppraisalType>(
    () => {
      const userResponse = results[initialIndex]?.userResponse
      return userResponse && userResponse !== 'IN_REVIEW' ? userResponse : null
    },
  )
  const [clickedEvidenceIndex, setClickedEvidenceIndex] = useState<
    number | null
  >(() => {
    const hasEvidence = results[initialIndex]?.result?.evidence?.length > 0
    return hasEvidence ? 0 : null
  })

  const updateQueryAppraisal = useUpdateQueryAppraisal()

  const currentResult = results[currentIndex]
  const totalResults = results.length

  useEffect(() => {
    const firstEvidence = results[initialIndex]?.result?.evidence?.[0]
    if (firstEvidence) {
      setPDFSearchState(firstEvidence.quote, firstEvidence.location?.[0]?.page)
    }
  }, [])

  const markQueryInReview = (queryResult: ISiteBoxDocumentConfigResult) => {
    if (queryResult.userResponse) return

    updateQueryAppraisal.mutate({
      documentId,
      queryId: queryResult.id,
      appraisal: {
        userResponse: 'IN_REVIEW',
        additionalComment: '',
      },
    })
  }

  const saveAppraisal = (nextResult?: ISiteBoxDocumentConfigResult | null) => {
    if (!selectedResponse || !currentResult) {
      if (nextResult) markQueryInReview(nextResult)
      return
    }

    updateQueryAppraisal.mutate(
      {
        documentId,
        queryId: currentResult.id,
        appraisal: {
          userResponse: selectedResponse,
          additionalComment: comment,
        },
      },
      {
        onSuccess: () => {
          if (nextResult) markQueryInReview(nextResult)
        },
      },
    )
  }

  const syncStateToResult = (result: ISiteBoxDocumentConfigResult) => {
    const userResponse = result?.userResponse
    setSelectedResponse(
      userResponse && userResponse !== 'IN_REVIEW' ? userResponse : null,
    )
    setComment(result?.additionalComment || '')

    const firstEvidence = result?.result?.evidence?.[0]
    if (firstEvidence) {
      setClickedEvidenceIndex(0)
      setPDFSearchState(firstEvidence.quote, firstEvidence.location?.[0]?.page)
    } else {
      setClickedEvidenceIndex(null)
      clearPDFSearchState()
    }
  }

  const handleQueryResultChange = (nextIndex: number) => {
    const isComplete = nextIndex === totalResults
    const nextResult = isComplete ? null : results[nextIndex]

    if (!isDocumentAppraised && canUserAppraiseDocument) {
      saveAppraisal(nextResult)
    }

    if (isComplete) {
      onComplete()
      return
    }

    syncStateToResult(nextResult)
    setCurrentIndex(nextIndex)
  }

  const handleBackClick = () => {
    if (!isDocumentAppraised && canUserAppraiseDocument) {
      saveAppraisal()
    }
    clearPDFSearchState()
    setClickedEvidenceIndex(null)
    onBackClick()
  }

  const handleEvidenceClick = (
    index: number,
    quote: string,
    pageNumber?: number,
  ) => {
    setClickedEvidenceIndex(index)
    setPDFSearchState(quote, pageNumber)
  }

  return (
    <Flex flexDirection='column' height='100%' px={2}>
      <Box height={44} display='flex' alignItems='center'>
        <IconButton onClick={handleBackClick}>
          <ArrowBack />
        </IconButton>
        <Text fontSize={12}>
          {localizedStrings.appraisalsByTemplateName(
            documentEngineTemplateName,
          )}
        </Text>
      </Box>

      <Box flex={1} overflow='auto'>
        <QueryResultDetails
          result={currentResult}
          clickedEvidenceIndex={clickedEvidenceIndex}
          onEvidenceClick={handleEvidenceClick}
          searchResultInfo={searchResultInfo}
          onNextResult={onNextResult}
          onPreviousResult={onPreviousResult}
        />
      </Box>

      <Box
        p={2}
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
        }}
      >
        <Text gutterBottom fontWeight={500}>
          {localizedStrings.response}
        </Text>

        <Box mb={2}>
          <AppraisalStatusButtons
            value={selectedResponse}
            onChange={setSelectedResponse}
            variant='query'
            size='small'
            isDocumentAppraised={
              isDocumentAppraised || !canUserAppraiseDocument
            }
          />
        </Box>

        <TextInput
          fullWidth
          multiline
          disabled={isDocumentAppraised || !canUserAppraiseDocument}
          rows={2}
          size='small'
          label={localizedStrings.comment}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          sx={{ mb: 2 }}
        />
        <Flex justifyContent='space-between' alignItems='center'>
          <Button
            variant='outlined'
            startIcon={<KeyboardArrowLeft />}
            onClick={() => handleQueryResultChange(currentIndex - 1)}
            disabled={currentIndex === 0}
          >
            {localizedStrings.previous}
          </Button>

          <Text variant='body2'>
            {currentIndex + 1} / {totalResults}
          </Text>

          <Button
            variant='outlined'
            endIcon={<KeyboardArrowRight />}
            onClick={() => handleQueryResultChange(currentIndex + 1)}
          >
            {localizedStrings.next}
          </Button>
        </Flex>
      </Box>
    </Flex>
  )
}
